import { useState } from 'react';
import { ArrowLeft, Upload, FileText, X, Trash2, Plus, Save } from 'lucide-react';

export default function ExpertiseManagement() {
  
  const [expertise, setExpertise] = useState({
    areas: ['Crop Science', 'Sustainable Agriculture', 'Soil Management'],
    credentials: [
      {
        id: 1,
        name: 'PhD Certificate in Agricultural Sciences',
        date: 'Uploaded on 15 Jan 2020',
        file: 'phd_certificate.pdf'
      },
      {
        id: 2,
        name: 'Agrictech  Certification',
        date: 'Uploaded on 22 Mar 2021',
        file: 'Agrictech _cert.pdf'
      },
      {
        id: 3,
        name: 'Professional License',
        date: 'Uploaded on 5 Nov 2022',
        file: 'license.pdf'
      }
    ]
  });

  
  const [newArea, setNewArea] = useState('');
  const [showAddArea, setShowAddArea] = useState(false);


  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  
  const handleAddArea = () => {
    if (newArea.trim() !== '') {
      setExpertise({
        ...expertise,
        areas: [...expertise.areas, newArea.trim()]
      });
      setNewArea('');
      setShowAddArea(false);
    }
  };

  const handleRemoveArea = (index) => {
    const updatedAreas = [...expertise.areas];
    updatedAreas.splice(index, 1);
    setExpertise({
      ...expertise,
      areas: updatedAreas
    });
  };

 
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsUploading(true);
     
      setTimeout(() => {
        const newCredential = {
          id: expertise.credentials.length + 1,
          name: file.name.replace(/\.[^/.]+$/, ""), // Remove file extension
          date: `Uploaded on ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`,
          file: file.name
        };
        setExpertise({
          ...expertise,
          credentials: [...expertise.credentials, newCredential]
        });
        setIsUploading(false);
      }, 1500);
    }
  };

  
  const handleDeleteCredential = (id) => {
    setExpertise({
      ...expertise,
      credentials: expertise.credentials.filter(cred => cred.id !== id)
    });
  };

  // API service function
  const updateExpertDetails = async (data) => {
    try {
      const response = await fetch('/expert/update-details', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update expert details');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating expert details:', error);
      throw error;
    }
  };

  const saveExpertiseChanges = async () => {
    setIsLoading(true);
    try {
      const updateData = {
        areas_of_expertise: expertise.areas,
        credentials: expertise.credentials.map(cred => cred.name).join(', ')
      };

      const result = await updateExpertDetails(updateData);
      
      if (result.success) {
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
      }
    } catch (error) {
      console.error('Error saving expertise changes:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="account-settings-container-apss">
      <div className="account-settings-header-apss">
        <button className="back-button-apss" onClick={() => window.history.back()}>
          <ArrowLeft size={20} />
          <span>Back to Settings</span>
        </button>
        <h1 className="account-settings-title-apss">Expertise Management</h1>
      </div>

      {/* Areas of Expertise Section */}
      <div className="settings-section-apss">
        <h3 className="section-title-apss">Areas of Expertise</h3>
        <div className="section-content-apss">
          <div className="expertise-list-apss">
            {expertise.areas.map((area, index) => (
              <div key={index} className="expertise-item-apss">
                <span>{area}</span>
                <button 
                  className="remove-expertise-button-apss"
                  onClick={() => handleRemoveArea(index)}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
          
          {showAddArea ? (
            <div className="add-expertise-container-apss">
              <input
                type="text"
                value={newArea}
                onChange={(e) => setNewArea(e.target.value)}
                placeholder="Enter new area of expertise"
                className="expertise-input-apss"
              />
              <div className="add-expertise-actions-apss">
                <button 
                  className="save-expertise-button-apss"
                  onClick={handleAddArea}
                >
                  Add
                </button>
                <button 
                  className="cancel-expertise-button-apss"
                  onClick={() => setShowAddArea(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button 
              className="add-expertise-button-apss"
              onClick={() => setShowAddArea(true)}
            >
              <Plus size={16} />
              <span>Add Area of Expertise</span>
            </button>
          )}
        </div>
      </div>

      {/* Credentials Section */}
      <div className="settings-section-apss">
        <h3 className="section-title-apss">Credentials & Certifications</h3>
        <p className="section-description-apss">
          Upload documents that verify your expertise (PDF, JPG, or PNG, max 5MB each)
        </p>
        
        <div className="credentials-list-apss">
          {expertise.credentials.map(credential => (
            <div key={credential.id} className="credential-item-apss">
              <div className="credential-icon-apss">
                <FileText size={20} />
              </div>
              <div className="credential-info-apss">
                <div className="credential-name-apss">{credential.name}</div>
                <div className="credential-date-apss">{credential.date}</div>
              </div>
              <div className="credential-actions-apss">
                <a 
                  href={`/download/${credential.file}`} 
                  className="download-credential-button-apss"
                  download
                >
                  Download
                </a>
                <button 
                  className="delete-credential-button-apss"
                  onClick={() => handleDeleteCredential(credential.id)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="upload-credential-container-apss">
          <label className="upload-credential-button-apss">
            <input 
              type="file" 
              onChange={handleFileUpload}
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: 'none' }}
            />
            <Upload size={16} />
            <span>{isUploading ? 'Uploading...' : 'Upload New Credential'}</span>
          </label>
        </div>
      </div>

      {/* Save Changes Button */}
      <div className="save-changes-container-apss">
        <button 
          className="save-all-changes-button-apss"
          onClick={saveExpertiseChanges}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Expertise Changes'}
        </button>
      </div>

      {/* Success Alert */}
      {showSuccessAlert && (
        <div className="success-alert-right-apss">
          <div className="success-alert-content-apss">
            <div className="success-alert-icon-apss">✓</div>
            <div className="success-alert-message-apss">
              Expertise details updated successfully!
            </div>
          </div>
        </div>
      )}
    </div>
  );
}